import React, { useEffect, useState, useMemo, useCallback } from "react";
import PropTypes from "prop-types";
import { useParams, useHistory } from "react-router-dom";
import { useMutation, useQuery, useApolloClient, useLazyQuery } from "@apollo/client";
import {
  Grid,
  Typography,
  useTheme,
  useMediaQuery
} from "@mui/material";
import { Button, Icon } from "rbx";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faBars } from "@fortawesome/free-solid-svg-icons";

import {
  deepOmit,
  customToast as toast,
  uppercaseFirstLetter,
  stripTimeZone,
  downloadPdfFromDataUrl,
} from "../../utils";
import { Loader, Confirmation, ModalWrapper } from "../../components";
import {
  DEFAULT_DECCO_U_COLUMNS,
  getSkillsColumns,
  getLicenseColumns,
  getCertificationColumns,
  generateColumns,
} from "./columns";
import {
  LicenseForm,
  CertificationForm,
  SkillForm,
  DeccoUForm,
  BaseballCardHeader,
  BaseballCardAvatar,
  BaseballCardButtons,
  BaseballCardTable,
  BaseballCardTrainings,
} from "./components";

import { usePermissions } from "../../hooks";
import { useModal, useAuth } from "../../context";

import {
  UPDATE_USER_MUTATION,
  GET_BASEBALL_CARD_EMPLOYEE,
  FIND_FIRST_AVERAGE_POINTS,
  GET_PROJECT,
  SIGNED_FILE_URL_QUERY,
  GET_WPQ_BY_EMPLOYEE_NUMBER,
  GET_WPQ_PDF,
  SEND_WPQ_RENEW_EMAIL,
} from "../../graphql";
import PrintBaseballCard from "./components/PrintBaseballCard";
import ProjectAssignmentHistory from "../ProjectAssignmentHistory";
import { FIND_MANY_DOCUMENTS } from "../../graphql/resources";

import "./BaseballCard.scss";

const TAB_STATES = {
  licenses: "licenses",
  certifications: "certifications",
  skills: "skills",
  wpq: "wpq",
};

const isExpirationWithinOneMonth = (expirationDate) => {
  if (!expirationDate) return false;
  const exp = new Date(expirationDate);
  if (Number.isNaN(exp.getTime())) return false;
  const now = new Date();
  const oneMonthFromNow = new Date(now);
  oneMonthFromNow.setMonth(oneMonthFromNow.getMonth() + 1);
  return exp <= oneMonthFromNow;
};

const buildWpqColumns = (handleDownload, canDownload, onSendRenewEmail) => {
  /* eslint-disable react/prop-types */
  const baseColumns = [
    {
      Header: "Name",
      accessor: "name",
    },
    {
      Header: "WPQ Procedure",
      accessor: "wpqProcedure",
    },
    {
      Header: "Expiration Date",
      accessor: "expirationDate",
      // eslint-disable-next-line react/prop-types
      Cell: ({ cell }) => {
        if (!cell?.value) return "--";
        const d = new Date(cell.value);
        if (Number.isNaN(d.getTime())) return "--";
        const isExpired = d < new Date();
        const mm = String(d.getMonth() + 1).padStart(2, "0");
        const dd = String(d.getDate()).padStart(2, "0");
        const yyyy = d.getFullYear();
        return (
          <span style={{ color: isExpired ? "red" : undefined }}>
            {`${mm}/${dd}/${yyyy}`}
          </span>
        );
      },
    },
    {
      Header: "Points",
      accessor: "points",
      // eslint-disable-next-line react/prop-types
      Cell: ({ cell }) =>
        cell?.value || cell?.value === 0 ? cell.value : "--",
    },
  ];
  /* eslint-enable react/prop-types */

  const columnsAfterBase = [];

  if (canDownload) {
    columnsAfterBase.push({
      Header: "Download",
      id: "download",
      // eslint-disable-next-line react/prop-types
      Cell: ({ row }) => (
        <Button
          color="info"
          size="small"
          style={{ backgroundColor: "#052e6f", borderColor: "#052e6f" }}
          onClick={(e) => {
            e.stopPropagation();
            // eslint-disable-next-line react/prop-types
            handleDownload?.(row?.original?.externalId);
          }}
        >
          Download
        </Button>
      ),
    });
  }

  const RenewExpirationDateCell = ({ row }) => {
    const wpqId = row?.original?.id;
    const expirationDate = row?.original?.expirationDate;
    const canSend = wpqId && isExpirationWithinOneMonth(expirationDate);
    return (
      <Button
        color="info"
        disabled={!canSend}
        size="small"
        style={{
          backgroundColor: canSend ? "#052e6f" : "#9ca3af",
          borderColor: canSend ? "#052e6f" : "#6b7280",
          color: canSend ? undefined : "#374151",
          cursor: canSend ? "pointer" : "not-allowed",
        }}
        onClick={(e) => {
          e.stopPropagation();
          if (canSend) onSendRenewEmail?.(wpqId);
        }}
      >
        Send Renew Email
      </Button>
    );
  };
  RenewExpirationDateCell.propTypes = {
    row: PropTypes.shape({
      original: PropTypes.shape({
        id: PropTypes.string,
        expirationDate: PropTypes.oneOfType([
          PropTypes.string,
          PropTypes.instanceOf(Date),
        ]),
      }),
    }).isRequired,
  };

  columnsAfterBase.push({
    Header: "Renew Expiration Date",
    id: "renewExpirationDate",
    Cell: RenewExpirationDateCell,
  });

  return [...baseColumns, ...columnsAfterBase];
};

const BaseballCard = () => {
  const [fileName, setFileName] = useState("");
  const theme = useTheme();
  const downSm = useMediaQuery(theme.breakpoints.down("sm"));
  const { state: authState, setState: setContext } = useAuth();

  const { setModalOpen } = useModal();
  const history = useHistory();
  const [tabState, setTabState] = useState("licenses");
  const [editMode, setEditMode] = useState(false);
  const [profileImageS3, setProfileImageS3] = useState("");
  const [printMode, setPrintMode] = useState(false);
  const [uploadProfilePermissions, setUploadProfilePermissions] =
    useState(false);


  const [updateUser] = useMutation(UPDATE_USER_MUTATION);
  const [editLicensePermissions, setEditLicensePermissions] = useState(false);
  const [editCertificationPermissions, setEditCertificationPermissions] =
    useState(false);
  const [editSkillPermissions, setEditSkillPermissions] = useState(false);
  const [editDeccoUPermissions, setEditDeccoUPermissions] = useState(false);
  const [exportBBCardPermissions, setExportBBCardPermissions] = useState(false);
  const [mobileButtonsOpen, setMobileButtonsOpen] = useState(false);

  const params = useParams();
  const { id } = params;

  const permissionLevels = usePermissions();

  const canDownloadWpq =
    authState?.user?.group === "D1" || authState?.user?.group === "D2";

  useEffect(() => {
    // prevent users from accessing other users baseball cards
    const currentUserNumber = authState?.user?.employeeNumber?.trim();
    const role = authState?.user?.userSecurities?.[0];
    const isPrivileged = [
      "superAdmin",
      "webEditor",
      "fieldSupervisor",
      "admin",
    ].includes(role);

    if (currentUserNumber && id && currentUserNumber !== id) {
      if (!isPrivileged) {
        history.push(`/baseball-card/${currentUserNumber}`);
      }
    }
  }, [authState?.user?.employeeNumber, authState?.user?.userSecurities, id, history]);

  const client = useApolloClient();

  const [employeeData, setEmployeeData] = useState({ wpq: [] });
  const [fetchWpq, { loading: wpqLoading }] = useLazyQuery(GET_WPQ_BY_EMPLOYEE_NUMBER, {
    fetchPolicy: "network-only",
  });
  const [wpqPointsTotal, setWpqPointsTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [qualificationOptions, setQualificationOptions] = useState([]);
  const [projectName, setProjectName] = useState("Not found");

  const { refetch } = useQuery(GET_BASEBALL_CARD_EMPLOYEE, {
    variables: {
      where: {
        employeeNumber: {
          contains: id.trim(),
        },
      },
    },
    fetchPolicy: "network-only",
    onCompleted: ({ getEmployeeBaseballCard: data }) => {
      const omitted = deepOmit(data, ["__typename"]);

      const formattedLicenses = omitted.licenses.map((license) => ({
        ...license,
        expDate: license.expDate ? stripTimeZone(license.expDate) : null,
        issueDate: license.issueDate ? stripTimeZone(license.issueDate) : null,
      }));

      const formattedCertifications = omitted.certifications.map(
        (certification) => ({
          ...certification,
          expDate: certification.expDate
            ? stripTimeZone(certification.expDate)
            : null,
          issueDate: certification.issueDate
            ? stripTimeZone(certification.issueDate)
            : null,
        })
      );

      const formattedDeccoUCourses = omitted.deccoUCourses.map(
        (deccoUCourse) => ({
          ...deccoUCourse,
          expDate: deccoUCourse.expDate ? deccoUCourse.expDate : null,
          issueDate: deccoUCourse.issueDate
            ? stripTimeZone(deccoUCourse.issueDate)
            : null,
        })
      );

      const formattedSkills = omitted.skills.map((skill) => ({
        ...skill,
        expDate: skill.expDate ? stripTimeZone(skill.issueDate) : null,
        issueDate: skill.issueDate ? stripTimeZone(skill.issueDate) : null,
      }));

      setEmployeeData((prev) => ({
        ...prev,
        ...omitted,
        firstName: uppercaseFirstLetter(omitted.firstName),
        lastName: uppercaseFirstLetter(omitted.lastName),
        employeeNumber: omitted?.employeeNumber?.trim(),
        licenses: formattedLicenses,
        certifications: formattedCertifications,
        deccoUCourses: formattedDeccoUCourses,
        skills: formattedSkills,
      }));
      setLoading(false);
    },
  });

  useEffect(() => {
    if (permissionLevels.webEditor) {
      setUploadProfilePermissions(true);
      setEditLicensePermissions(true);
      setEditCertificationPermissions(true);
      setEditSkillPermissions(true);
      setEditDeccoUPermissions(true);
      setExportBBCardPermissions(true);

      setQualificationOptions([
        "Add License",
        "Add Certification",
        "Add Skill",
        "Add DECCO U Course",
      ]);
    }

    if (permissionLevels.superAdmin) {
      setUploadProfilePermissions(false);
      setEditLicensePermissions(true);
      setEditCertificationPermissions(true);
      setEditSkillPermissions(true);
      setEditDeccoUPermissions(true);
      setExportBBCardPermissions(true);

      setQualificationOptions([
        "Add License",
        "Add Certification",
        "Add Skill",
        "Add DECCO U Course",
      ]);
    }

    if (permissionLevels.fieldSupervisor) {
      setQualificationOptions(["Add Skill"]);
      setEditSkillPermissions(true);
    }
  }, [permissionLevels, editMode]);

  useEffect(() => {
    if (employeeData) {
      const getAverageCompanyPoints = async () => {
        try {
          const { data } = await client.query({
            query: FIND_FIRST_AVERAGE_POINTS,
            variables: {
              orderBy: [
                {
                  timeStamp: "desc",
                },
              ],
            },
          });

          setEmployeeData((prev) => ({
            ...prev,
            averageCompanyPoints: data?.findFirstAveragePoints?.points,
          }));
        } catch (err) {
          toast.error("An error has occured, please try again later");
        }
      };

      getAverageCompanyPoints();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [client]);

  useEffect(() => {
    if (employeeData && employeeData?.projectId) {
      const getProject = async () => {
        try {
          const { data: ProjectData } = await client.query({
            query: GET_PROJECT,
            variables: {
              projectId: employeeData?.projectId,
            },
          });

          setProjectName(ProjectData?.getProject?.name);
        } catch (err) {
          toast.error("An error has occured, please try again later");
        }
      };

      getProject();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [client, employeeData]);

  useEffect(() => {
    const loadWpqRecords = async () => {
      try {
        if (!employeeData?.employeeNumber) return;
        const { data } = await fetchWpq({
          variables: {
            employeeNumber: employeeData.employeeNumber,
          },
        });

        const wpqRows = (data?.getWpqByEmployeeNumber || []).map((row) => ({
          ...row,
          // Use WPQ id from API for send renew email; fallback for display key
          id: row?.id || row?.externalId || row?.welderId || row?.name,
          // Show the WPS name we matched for points in the "WPQ Procedure" column
          wpqProcedure: row?.matchedWpsName || row?.wpqProcedure,
          points: row?.points ?? 0,
          expirationDate: row?.expirationDate || null,
        }));

        const now = new Date();
        const wpqPoints = wpqRows.reduce((sum, row) => {
          const exp = row?.expirationDate ? new Date(row.expirationDate) : null;
          const isValid =
            exp && !Number.isNaN(exp.getTime()) && exp > now;
          return sum + (isValid ? (row.points || 0) : 0);
        }, 0);

        setEmployeeData((prev) => ({
          ...prev,
          wpq: wpqRows,
        }));
        setWpqPointsTotal(wpqPoints);
      } catch (err) {
        toast.error("Failed to load WPQ records");
      }
    };

    loadWpqRecords();
  }, [employeeData?.employeeNumber, fetchWpq]);

  const handleDownloadWpq = useCallback(async (externalId) => {
    if (!externalId) return;
    try {
      const { data } = await client.query({
        query: GET_WPQ_PDF,
        variables: { externalId },
        fetchPolicy: "network-only",
      });
      const pdfData = data?.getWpqPdf;
      if (pdfData) {
        await downloadPdfFromDataUrl(pdfData, `WPQ_${externalId}.pdf`);
      } else {
        toast.error("WPQ PDF not available");
      }
    } catch (err) {
      toast.error("Unable to download WPQ PDF");
    }
  }, [client]);

  const [sendWpqRenewEmailMutation] = useMutation(SEND_WPQ_RENEW_EMAIL);
  const handleSendWpqRenewEmail = useCallback(
    async (wpqId) => {
      if (!wpqId) return;
      try {
        await sendWpqRenewEmailMutation({ variables: { wpqId } });
        toast.success("Renew email sent successfully.");
      } catch (err) {
        const message = err?.message || "Failed to send renew email.";
        toast.error(message);
      }
    },
    [sendWpqRenewEmailMutation]
  );

  const skillsColumns = useMemo(
    () => getSkillsColumns({ showAssignedBy: Boolean(authState?.user?.showAssignedBy) }),
    [authState?.user?.showAssignedBy]
  );

  const licenseColumns = useMemo(
    () => getLicenseColumns({ showAssignedBy: Boolean(authState?.user?.showAssignedBy) }),
    [authState?.user?.showAssignedBy]
  );

  const certificationColumns = useMemo(
    () => getCertificationColumns({ showAssignedBy: Boolean(authState?.user?.showAssignedBy) }),
    [authState?.user?.showAssignedBy]
  );

  const COLUMNS = useMemo(() => {
    const handleDelete = async (qualId, type) => {
      const performDelete = async () => {
        try {
          await client.mutate({
            mutation: UPDATE_USER_MUTATION,
            variables: {
              where: {
                id: employeeData?.id,
              },
              data: {
                [type]: {
                  delete: [
                    {
                      id: qualId,
                    },
                  ],
                },
              },
              refetchQueries: ["getEmployeeBaseballCard", "getEmployees"],
              awaitRefetchQueries: true,
            },
          });
          toast.success("Qualification deleted succesfully.");
          refetch();
        } catch (err) {
          toast.error(
            "There was an error deleting the qualification. Please contact support."
          );
        } finally {
          setModalOpen(false);
        }
      };
      setModalOpen(
        true,
        <Confirmation
          message={`Are you sure you want to delete this ${type?.substring(
            0,
            type?.length - 1
          )}?`}
          onCancel={() => setModalOpen(false)}
          onConfirm={performDelete}
        />
      );
    };

    if (!editMode) {
      return {
        licenses: licenseColumns,
        certifications: certificationColumns,
        skills: skillsColumns,
        deccoUCourses: DEFAULT_DECCO_U_COLUMNS,
        wpq: buildWpqColumns(handleDownloadWpq, canDownloadWpq, handleSendWpqRenewEmail),
      };
    }

    if (editMode) {
      if (permissionLevels.superAdmin || permissionLevels.webEditor) {
        return {
          licenses: generateColumns(
            handleDelete,
            refetch,
            licenseColumns
          ),
          certifications: generateColumns(
            handleDelete,
            refetch,
            certificationColumns
          ),
          skills: generateColumns(
            handleDelete,
            refetch,
            skillsColumns
          ),
          deccoUCourses: generateColumns(
            handleDelete,
            refetch,
            DEFAULT_DECCO_U_COLUMNS
          ),
          wpq: buildWpqColumns(handleDownloadWpq, canDownloadWpq, handleSendWpqRenewEmail),
        };
      }
    }

    if (permissionLevels.fieldSupervisor) {
      return {
        skills: generateColumns(handleDelete, refetch, skillsColumns),
      };
    }
    return <p>Not Authorized</p>;
  }, [
    client,
    editMode,
    employeeData?.id,
    skillsColumns,
    licenseColumns,
    certificationColumns,
    handleDownloadWpq,
    canDownloadWpq,
    handleSendWpqRenewEmail,
    permissionLevels.fieldSupervisor,
    permissionLevels.superAdmin,
    permissionLevels.webEditor,
    refetch,
    setModalOpen,
  ]);

  const handleRowClick = (inputState) => {
    if (tabState === "licenses" && editLicensePermissions) {
      setModalOpen(
        true,
        <LicenseForm
          employeeData={employeeData}
          initialInputs={{
            expDate: inputState?.expDate ? inputState?.expDate : "",
            issueDate: inputState?.issueDate ? inputState?.issueDate : "",
            licenseNumber: inputState?.licenseNumber,
            id: inputState?.id,
            title: `${inputState?.codes?.title}  ${inputState?.codes?.state} ${inputState?.codes?.level}`,
            expirationType: inputState?.codes?.expirationType,
          }}
          mobile={downSm}
          onCancel={() => setModalOpen(false, "")}
          onComplete={async (data) => {
            try {
              await updateUser({
                variables: {
                  where: {
                    id: employeeData?.id,
                  },
                  data,
                  refetchQueries: "active",
                  awaitRefetchQueries: true,
                },
                onCompleted: () => {
                  refetch();
                },
              });
              toast.success("Successfully updated employee");
              setModalOpen(false, "");
            } catch (err) {
              toast.error("An error has occured, please try again later");
            }
          }}
        />
      );
    } else if (tabState === "certifications" && editCertificationPermissions) {
      setModalOpen(
        true,
        <CertificationForm
          employeeData={employeeData}
          initialInputs={{
            expDate: inputState?.expDate ? inputState?.expDate : "",
            issueDate: inputState?.issueDate ? inputState?.issueDate : "",
            certificationNumber: inputState?.certificationNumber,
            id: inputState?.id,
            title: `${inputState?.codes?.title}  ${inputState?.codes?.state} ${inputState?.codes?.level}`,
            expirationType: inputState?.codes?.expirationType,
          }}
          mobile={downSm}
          onCancel={() => setModalOpen(false, "")}
          onComplete={async (data) => {
            try {
              await updateUser({
                variables: {
                  where: {
                    id: employeeData?.id,
                  },
                  data,
                  refetchQueries: "active",
                  awaitRefetchQueries: true,
                },
                onCompleted: () => {
                  refetch();
                },
              });
              toast.success("Successfully updated employee");
              setModalOpen(false, "");
            } catch (err) {
              toast.error("An error has occured, please try again later");
            }
          }}
        />
      );
    } else if (tabState === "skills" && editSkillPermissions) {
      setModalOpen(
        true,
        <SkillForm
          employeeData={employeeData}
          initialInputs={{
            expDate: inputState?.expDate ? inputState?.expDate : "",
            issueDate: inputState?.issueDate ? inputState?.issueDate : "",
            certificationNumber: inputState?.certificationNumber,
            id: inputState?.id,
            title: `${inputState?.codes?.title}  ${inputState?.codes?.state} ${inputState?.codes?.level}`,
            expirationType: inputState?.codes?.expirationType,
          }}
          mobile={downSm}
          onCancel={() => setModalOpen(false, "")}
          onComplete={async (data) => {
            try {
              await updateUser({
                variables: {
                  where: {
                    id: employeeData?.id,
                  },
                  data,
                  refetchQueries: "active",
                  awaitRefetchQueries: true,
                },
                onCompleted: () => {
                  refetch();
                },
              });
              toast.success("Successfully updated employee");
              setModalOpen(false, "");
            } catch (err) {
              toast.error("An error has occured, please try again later");
            }
          }}
        />
      );
    }
  };

  const handleUpdateDeccoUCourse = (inputState) => {
    if (editDeccoUPermissions) {
      setModalOpen(
        true,
        <DeccoUForm
          employeeData={employeeData}
          initialInputs={{
            expDate: inputState?.expDate ? inputState?.expDate : "",
            issueDate: inputState?.issueDate ? inputState?.issueDate : "",
            certificationNumber: inputState?.certificationNumber,
            id: inputState?.id,
            title: `${inputState?.codes?.title}  ${inputState?.codes?.state} ${inputState?.codes?.level}`,
            expirationType: inputState?.codes?.expirationType,
          }}
          mobile={downSm}
          onCancel={() => setModalOpen(false, "")}
          onComplete={async (data) => {
            // if any values are dates, format them

            try {
              await updateUser({
                variables: {
                  where: {
                    id: employeeData?.id,
                  },
                  data,
                  refetchQueries: "active",
                  awaitRefetchQueries: true,
                },
                onCompleted: () => {
                  refetch();
                },
              });
              toast.success("Successfully updated employee");
              setModalOpen(false, "");
            } catch (err) {
              toast.error("An error has occured, please try again later");
            }
          }}
        />
      );
    }
  };

  const handleAddClick = (type) => {
    if (type === "Add DECCO U Course") {
      setModalOpen(
        true,
        <DeccoUForm
          employeeData={employeeData}
          mobile={downSm}
          onCancel={() => setModalOpen(false, "")}
          onComplete={async (data) => {
            try {
              setLoading(true);
              await updateUser({
                variables: {
                  where: {
                    id: employeeData?.id,
                  },
                  data,
                  refetchQueries: "active",
                  awaitRefetchQueries: true,
                },
              });
            } catch (err) {
              toast.error("An error has occured, please try again later");
            } finally {
              refetch();
              toast.success("Successfully created course");

              setModalOpen(false, "");
              setLoading(false);
            }
          }}
        />
      );
    } else if (type === "Add Certification") {
      setModalOpen(
        true,
        <CertificationForm
          employeeData={employeeData}
          mobile={downSm}
          onCancel={() => setModalOpen(false, "")}
          onComplete={async (data) => {
            try {
              setLoading(true);
              await updateUser({
                variables: {
                  where: {
                    id: employeeData?.id,
                  },
                  data,
                  refetchQueries: "active",
                  awaitRefetchQueries: true,
                },
              });
            } catch (err) {
              toast.error("An error has occured, please try again later");
            } finally {
              refetch();
              toast.success("Successfully created certification");

              setModalOpen(false, "");
              setLoading(false);
            }
          }}
        />
      );
    } else if (type === "Add Skill") {
      setModalOpen(
        true,
        <SkillForm
          employeeData={employeeData}
          mobile={downSm}
          onCancel={() => setModalOpen(false, "")}
          onComplete={async (data) => {
            try {
              setLoading(true);
              await updateUser({
                variables: {
                  where: {
                    id: employeeData?.id,
                  },
                  data,
                  refetchQueries: "active",
                  awaitRefetchQueries: true,
                },
              });
            } catch (err) {
              toast.error("An error has occured, please try again later");
            } finally {
              refetch();
              toast.success("Successfully created skill");

              setModalOpen(false, "");
              setLoading(false);
            }
          }}
        />
      );
    } else {
      setModalOpen(
        true,
        <LicenseForm
          employeeData={employeeData}
          mobile={downSm}
          onCancel={() => setModalOpen(false, "")}
          onComplete={async (data) => {
            try {
              setLoading(true);
              await updateUser({
                variables: {
                  where: {
                    id: employeeData?.id,
                  },
                  data,
                  refetchQueries: "active",
                  awaitRefetchQueries: true,
                },
              });
            } catch (err) {
              toast.error("An error has occured, please try again later");
            } finally {
              refetch();
              toast.success("Successfully created license");

              setModalOpen(false, "");
              setLoading(false);
            }
          }}
        />
      );
    }
  };

  const handleUploadImageChange = async (response) => {
    const { filepath } = response;
    try {
      await updateUser({
        variables: {
          data: {
            profileImage: { set: filepath },
          },
          where: {
            id: employeeData?.id,
          },
        },
        onCompleted: () => {
          refetch();
        },
      });

      setFileName(filepath);
      if (authState?.user.id === employeeData.userId) {
        setContext((prev) => ({
          ...prev,
          user: { ...prev.user, profileImage: filepath },
        }));
      }
      toast.success("Profile Image Saved Successfully");
    } catch (err) {
      toast.error("An error has occured, please try again later");
    }
  };

  const onEditClick = () => {
    setEditMode(!editMode);
  };

  const openAssignmentHistory = () => {
    setModalOpen(
      true,
      <ModalWrapper
        details=""
        title="Project Assignment History"
        onComplete={() => setModalOpen(false)}
      >
        <ProjectAssignmentHistory userId={employeeData?.id} />
      </ModalWrapper>
    );
  };

  const handlePrintClick = () => {
    setPrintMode(true);
  };

  useEffect(() => {
    if (employeeData?.profileImage) {
      const handleDownloadProfilePicture = async (data) => {
        const { profileImage } = data;
        const path = profileImage.split("/")[4];
        const { data: signedFileUrlData } = await client.query({
          query: SIGNED_FILE_URL_QUERY,
          variables: {
            input: {
              objectKey: path,
            },
            fetchPolicy: "network-only",
          },
        });
        const { signedFileUrl } = signedFileUrlData;
        setProfileImageS3(signedFileUrl);
      };
      handleDownloadProfilePicture(employeeData);
    }
  }, [employeeData, fileName, client]);

  const handleDownloadCellPhoneList = async () => {
    let objectKey = "DECCOContactInfo.pdf";
    let source = "fallback (no document or error)";
    let latestDoc = null;
    try {
      const { data: docsData } = await client.query({
        query: FIND_MANY_DOCUMENTS,
        variables: {
          where: { description: { equals: "DECCO Cell Phone List" } },
          orderBy: [{ createdAt: "desc" }],
        },
      });
      const docs = docsData?.findManyDocuments || [];
      if (docs.length > 0 && docs[0].path) {
        // eslint-disable-next-line prefer-destructuring
        latestDoc = docs[0];
        const segments = docs[0].path.split("/").filter(Boolean);
        const blobName = segments[segments.length - 1];
        if (blobName) {
          objectKey = blobName;
          source = "latest document from Resources";
        }
      }
    } catch (err) {
      console.warn("[DECCO Cell Phone List] Could not load document list:", err);
    }
    console.log("[DECCO Cell Phone List] Blob being fetched:", {
      objectKey,
      source,
      latestDoc: latestDoc ? { id: latestDoc.id, path: latestDoc.path, createdAt: latestDoc.createdAt, name: latestDoc.name } : null,
    });
    const { data: signedFileUrlData } = await client.query({
      query: SIGNED_FILE_URL_QUERY,
      variables: {
        input: { objectKey },
      },
    });
    const { signedFileUrl } = signedFileUrlData;
    window.open(signedFileUrl);
  };


  if (printMode) {
    return (
      <PrintBaseballCard
        employeeData={employeeData}
        profileImageS3={profileImageS3}
        licenseColumns={licenseColumns}
        certificationColumns={certificationColumns}
        skillsColumns={skillsColumns}
        onComplete={() => {
          window.print();
          setLoading(false);
          setPrintMode(false);
        }}
      />
    );
  }

  const tableLoading = tabState === "wpq" ? wpqLoading : loading;

  return (
    <Grid container className="baseball-card-container2" spacing={2}>
      {loading ? (
        <div style={{ position: "absolute", top: "40%", left: "50%" }}>
          <Loader />
        </div>
      ) : (
        <React.Fragment>
          <Grid
            item
            maxHeight="100px"
            md={2}
            style={{ zIndex: 1 }}
            sx={{ display: { xs: "none", sm: "none", md: "inline" } }}
            xs={0}
          >
            <BaseballCardAvatar
              employeeData={employeeData}
              profileImageS3={profileImageS3}
            />
            <div className="bball-card-buttons-container">
              <Grid container className="user-title">
                <Grid item display="flex" md={12} xs={10}>
                  <Grid container>
                    <Grid item display="flex">
                      <Typography
                        className="c-secondary"
                        fontWeight="bold"
                        mr={1}
                      >
                        Username
                      </Typography>
                      <Typography>{employeeData?.email || ""}</Typography>
                    </Grid>
                    <Grid item display="flex">
                      <Typography
                        className="c-secondary"
                        fontWeight="bold"
                        mr={1}
                      >
                        Access
                      </Typography>
                      <Typography>
                        {employeeData?.userType || "user"}
                      </Typography>
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
              <BaseballCardButtons
                editMode={editMode}
                employeeData={employeeData}
                fileName={fileName}
                handleAddClick={handleAddClick}
                handleDownloadCellPhoneList={handleDownloadCellPhoneList}
                handleUploadImageChange={handleUploadImageChange}
                openAssignmentHistory={openAssignmentHistory}
                permissionLevels={permissionLevels}
                qualificationOptions={qualificationOptions}
                setLoading={setLoading}
                setModalOpen={setModalOpen}
                uploadProfilePermissions={uploadProfilePermissions}
                onEditClick={onEditClick}
              />
            </div>
          </Grid>
          <Grid item md={10} xs={12}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <BaseballCardHeader
                  employeeData={employeeData}
                  exportBBCardPermissions={exportBBCardPermissions}
                  handlePrintClick={handlePrintClick}
                  mobile={downSm}
                  profileImageS3={profileImageS3}
                  projectName={projectName}
                  wpqPointsTotal={wpqPointsTotal}
                />
              </Grid>
              <Grid
                item
                className="bball-card-buttons-container"
                sx={{ display: { xs: "inherit", md: "none" } }}
                xs={12}
              >
                <Grid container className="user-title">
                  <Grid item display="flex" md={12} xs={10}>
                    <Grid container>
                      <Grid item display="flex" xs={12}>
                        <Typography
                          className="c-secondary"
                          fontWeight="bold"
                          mr={1}
                        >
                          Username
                        </Typography>
                        <Typography>{employeeData?.email || ""}</Typography>
                      </Grid>
                      <Grid item display="flex" xs={12}>
                        <Typography
                          className="c-secondary"
                          fontWeight="bold"
                          mr={1}
                        >
                          Access
                        </Typography>
                        <Typography>
                          {employeeData?.userType || "user"}
                        </Typography>
                      </Grid>
                    </Grid>
                  </Grid>
                  <Grid
                    item
                    display="flex"
                    justifyContent="space-between"
                    md={0}
                    sm={2}
                  >
                    <Button
                      size="large"
                      onClick={() => setMobileButtonsOpen(!mobileButtonsOpen)}
                    >
                      <Icon className="c-secondary">
                        <FontAwesomeIcon icon={faBars} />
                      </Icon>
                    </Button>
                  </Grid>
                  <Grid item>
                    {mobileButtonsOpen ? (
                      <BaseballCardButtons
                        editMode={editMode}
                        employeeData={employeeData}
                        fileName={fileName}
                        handleAddClick={handleAddClick}
                        handleDownloadCellPhoneList={
                          handleDownloadCellPhoneList
                        }
                        handleUploadImageChange={handleUploadImageChange}
                        mobile={downSm}
                        openAssignmentHistory={openAssignmentHistory}
                        permissionLevels={permissionLevels}
                        qualificationOptions={qualificationOptions}
                        setLoading={setLoading}
                        setModalOpen={setModalOpen}
                        uploadProfilePermissions={uploadProfilePermissions}
                        onEditClick={onEditClick}
                      />
                    ) : null}
                  </Grid>
                </Grid>
              </Grid>
              <Grid
                item
                md={1}
                sx={{ display: { xs: "none", md: "inherit" } }}
                xs={0}
              />
              <Grid item md={7} style={{ maxWidth: downSm ? "80%" : "100%" }} xs={12}>
                <BaseballCardTable
                  COLUMNS={COLUMNS}
                  TAB_STATES={TAB_STATES}
                  editMode={!downSm && editMode}
                  employeeData={employeeData}
                  handleRowClick={handleRowClick}
                  loading={tableLoading}
                  mobile={downSm}
                  permissionLevels={permissionLevels}
                  setTabState={setTabState}
                  tabState={tabState}
                  onWpqDownload={handleDownloadWpq}
                  onSendWpqRenewEmail={handleSendWpqRenewEmail}
                />
              </Grid>
              <Grid item md={4} xs={12}>
                <BaseballCardTrainings
                  COLUMNS={COLUMNS}
                  editMode={!downSm && editMode}
                  employeeData={employeeData}
                  handleUpdateDeccoUCourse={handleUpdateDeccoUCourse}
                />
              </Grid>
            </Grid>
          </Grid>
        </React.Fragment>
      )}
    </Grid>
  );
};

BaseballCard.propTypes = {};

export default BaseballCard;
